/**
 * MfaSettingsCard — the user's control surface for two-factor authentication,
 * mounted on Account Settings. Drives the authenticated MFA lifecycle:
 *
 *   - enrollment wizard: password proof → QR / manual secret → verify code.
 *     On success the one-time recovery codes are handed to the full-screen
 *     `/auth/mfa-recovery-codes` page. Enrollment KEEPS the session alive, so
 *     the user acknowledges the codes there over that same session; the page
 *     still lives outside ProtectedRoute so the irrecoverable codes can't be
 *     lost to an involuntary redirect.
 *   - enrolled management: regenerate codes (shown in-card, session preserved),
 *     turn MFA off (both step-up gated).
 *   - re-enrollment: rotating the authenticator while MFA stays ON. Offered by
 *     the post-recovery prompt (`recoveryCodeUsedAt`), which appears after a
 *     backup-code sign-in and is opt-in — a recovery code no longer disables
 *     MFA, so the user still has a working factor and this is an offer, not an
 *     obligation. Dismissible.
 *   - recovery_pending re-enrollment banner (now reached only via a support
 *     reset, not by using a recovery code).
 *   - self-service repair for a session stuck mid-transition.
 *
 * SECURITY: all MFA calls go through auth-api's untraced `authFetch` (NOT the
 * traced api.ts client), so codes/secret/OTP/password never enter the telemetry
 * apiTraces buffer. Nothing here logs them or places them in breadcrumbs.
 */

import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  AlertTriangle,
  Loader2,
  Wrench,
  Copy,
  Check,
  Smartphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authApi, type MfaStatusResponse, type MfaStepUpFactor } from '@/lib/auth-api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from '@/auth/hooks/useAuth';
import { useCoarsePointer } from '@/hooks/useCoarsePointer';
import { toast } from '@/components/ui/use-toast';
import { setAuthFlash } from '@/lib/auth-flash';
import { stashRecoveryCodes } from '@/lib/mfa-codes-handoff';
import { RecoveryCodesDisplay } from '@/auth/components/RecoveryCodesDisplay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

// Roles for which MFA is required (mirrors PRIVILEGED_MFA_ROLES on the server).
// The client only uses this to *nudge* — enforcement is server-side (the
// privileged gate keys off mfa_state_version, never a client check).
const PRIVILEGED_MFA_ROLES = ['admin', 'support', 'litcraft_admin'];

// The card's internal wizard pointer, distinct from the server-side MFA state.
// (Enrollment codes are shown on the full-screen page, not here; the in-card
// `codes` step is only reached from a regenerate, which keeps the session.)
type Wizard =
  | { kind: 'idle' }
  | { kind: 'setup-password'; reenroll: boolean }
  | {
      kind: 'setup-verify';
      secretCode: string;
      otpauthUri: string;
      // Absent on older API responses — the wizard simply omits the hint that
      // names which entry to look for.
      accountLabel?: string;
      issuer?: string;
      reenroll: boolean;
    }
  | { kind: 'codes'; codes: string[]; generatedAt?: string }
  | { kind: 'stepup'; action: 'disable' | 'regenerate' };

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong. Please try again.';
}

function errorCode(err: unknown): string | undefined {
  return typeof err === 'object' && err !== null && 'code' in err
    ? (err as { code?: string }).code
    : undefined;
}

function keepDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

/** Copy for the privileged "MFA required" banner — countdown under grace, hard block under enforced. */
function privilegedRequiredCopy(gate?: {
  mode: 'off' | 'grace' | 'enforced';
  enforcementDeadline: string | null;
}): ReactNode {
  if (gate?.mode === 'enforced') {
    return (
      <>
        Your account role <strong>requires</strong> two-factor authentication. Admin access is
        blocked until you turn it on — set it up now.
      </>
    );
  }
  if (gate?.mode === 'grace' && gate.enforcementDeadline) {
    const deadline = new Date(gate.enforcementDeadline);
    const days = Math.max(0, Math.ceil((deadline.getTime() - Date.now()) / 86_400_000));
    // Render the full local date+time (not just the date) so the shown instant
    // and the "N days left" count — both derived from the same instant in the
    // viewer's local frame — never disagree across timezones (a UTC-midnight
    // deadline would otherwise show the prior calendar day next to a day count
    // computed to the true instant).
    const deadlineLabel = deadline.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    return (
      <>
        Your account role <strong>requires</strong> two-factor authentication. Enable it by{' '}
        {deadlineLabel} ({days} day{days === 1 ? '' : 's'} left) to keep access to admin tools.
      </>
    );
  }
  // Reachable only for `grace` with no parsable deadline — the caller no longer
  // renders this notice at all when the gate is absent or `off`.
  return (
    <>
      Your account role <strong>requires</strong> two-factor authentication. Set it up now to keep
      access to admin tools.
    </>
  );
}

export function MfaSettingsCard() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { handleReauthRequired, user } = useAuth();
  const privilegedRequired = (user?.roles ?? []).some((r) => PRIVILEGED_MFA_ROLES.includes(r));

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.mfa.status(),
    queryFn: () => authApi.getMfaStatus(),
  });

  const [wizard, setWizard] = useState<Wizard>({ kind: 'idle' });
  const [repairNotice, setRepairNotice] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.mfa.status() });

  // A flow whose backend response revoked the session (disable, or a repair that
  // committed a stranded enrollment). Reset local auth state without a pointless
  // /auth/logout round-trip, stage a login-page message, and route to sign-in.
  const reauthTo = (message: string) => {
    setAuthFlash(message);
    handleReauthRequired();
    navigate('/auth/login');
  };

  const setupMutation = useMutation({
    mutationFn: (v: { currentPassword: string; totpCode?: string; recoveryCode?: string }) => {
      // A fresh enrollment sends no proof at all — there is no factor to prove.
      // Only a re-enrollment carries one, and exactly one of the two.
      if (v.totpCode) return authApi.mfaSetup(v.currentPassword, { totpCode: v.totpCode });
      if (v.recoveryCode)
        return authApi.mfaSetup(v.currentPassword, { recoveryCode: v.recoveryCode });
      return authApi.mfaSetup(v.currentPassword);
    },
    onSuccess: (res) =>
      setWizard({
        kind: 'setup-verify',
        secretCode: res.secretCode,
        otpauthUri: res.otpauthUri,
        accountLabel: res.accountLabel,
        issuer: res.issuer,
        // The SERVER decides which this is (it knows the real MFA state); the
        // response echoes it so the wizard's instructions match what actually
        // happened. Absent ⇒ a plain first enrollment.
        reenroll: res.mode === 'reenroll',
      }),
  });

  const dismissRecoveryNoticeMutation = useMutation({
    mutationFn: () => authApi.mfaDismissRecoveryNotice(),
    onSuccess: () => invalidate(),
  });

  // Declared ahead of verifyMutation and cancelWizard — both roll back a
  // stranded `enabling` with it.
  const abortSetupMutation = useMutation({
    mutationFn: () => authApi.mfaAbortSetup(),
  });

  /**
   * Roll back an `enabling` left behind by an enrollment that ended without
   * completing, then refetch. Best-effort: a 409 (nothing in progress) is
   * harmless and expected when there was no server state to undo.
   *
   * A RE-enrollment never entered `enabling` (the profile stays `enabled`
   * throughout), so there is nothing to roll back there — the abandoned Cognito
   * association simply expires and the live secret is untouched.
   */
  const rollbackEnablingIfAny = (wasReenroll: boolean) => {
    if (!wasReenroll) {
      abortSetupMutation.mutate(undefined, { onSettled: () => invalidate() });
    } else {
      invalidate();
    }
  };

  const verifyMutation = useMutation({
    mutationFn: (code: string) => authApi.mfaVerifySetup(code),
    onSuccess: (res) => {
      // Enrollment no longer revokes this session (see routes/auth/mfa.ts), so
      // the user stays signed in: hand the one-time codes to the full-screen
      // page and let them acknowledge over the SAME live session. The page is
      // still used — it has no nav chrome to click away through, which is what
      // stops someone dismissing irrecoverable codes by accident.
      stashRecoveryCodes(res.recoveryCodes, res.generatedAt);
      navigate('/auth/mfa-recovery-codes');
    },
    onError: (err) => {
      // These clear the setup cookie server-side, so re-entering a code is
      // futile — reflect the true (now transitional) state and leave the wizard
      // instead of looping. MFA_CODE_MISMATCH keeps the cookie → stay to retry.
      const code = errorCode(err);
      if (code === 'MFA_SETUP_EXPIRED' || code === 'MFA_SETUP_MISMATCH') {
        // Roll the profile back too, not just the UI. The cookie is gone but
        // the server is still `enabling`, so closing the wizard alone drops the
        // card to the "You started setting up two-factor authentication but
        // didn't finish" notice — and the user must click Repair, or wait out
        // the ~10-minute staleness cutoff, before they can even retry. That is
        // easy to reach honestly: work through a few wrong codes (each one
        // correctly refused without touching the cookie) and the 10-minute
        // setup cookie simply runs out underneath you.
        //
        // /setup/abort documents itself as the self-service unstick for exactly
        // this case, and is deliberately cookie-free so it still works once the
        // cookie has expired.
        rollbackEnablingIfAny(wizard.kind === 'setup-verify' && wizard.reenroll === true);
        setWizard({ kind: 'idle' });
      }
    },
  });

  const disableMutation = useMutation({
    mutationFn: (v: { currentPassword: string; factor: MfaStepUpFactor }) =>
      authApi.mfaDisable(v.currentPassword, v.factor),
    // Disable no longer signs the user out (see routes/auth/mfa.ts) — the
    // session is re-minted server-side. Return to the settings view, refetch
    // status so the card reflects the disabled state, and confirm.
    onSuccess: () => {
      setWizard({ kind: 'idle' });
      invalidate();
      toast({
        title: 'Two-factor authentication is off',
        description: 'Signing in now only needs your password. You can turn it back on any time.',
      });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: (v: { currentPassword: string; factor: MfaStepUpFactor }) =>
      authApi.mfaRegenerateRecoveryCodes(v.currentPassword, v.factor),
    onSuccess: (res) => {
      acknowledgeMutation.reset();
      setWizard({ kind: 'codes', codes: res.recoveryCodes, generatedAt: res.generatedAt });
    },
  });

  const acknowledgeMutation = useMutation({
    mutationFn: () => authApi.mfaAcknowledgeRecoveryCodes(),
    onSuccess: () => {
      invalidate();
      setWizard({ kind: 'idle' });
    },
  });

  const repairMutation = useMutation({
    mutationFn: () => authApi.mfaRepair(),
    onSuccess: (res) => {
      if (res.reauthRequired) {
        reauthTo('Your two-factor setup was finished. Please sign in again to continue.');
        return;
      }
      // `operator_attention` leaves the state unchanged and needs a human.
      //
      // `none` + still `enabling` is the OTHER no-op, and it used to be
      // completely silent: reconcileMfaState refuses to expire an enrollment
      // younger than the ~10-minute staleness cutoff (expiring a fresh one
      // would 409 a verify-setup still in flight), so the click changed
      // nothing, said nothing, and left the same panel on screen. Say so.
      setRepairNotice(
        res.action === 'operator_attention'
          ? 'This couldn’t be fixed automatically and needs a support reset. Please contact support.'
          : res.action === 'none' && res.state === 'enabling'
            ? 'That setup was started very recently, so it can’t be cleared yet. ' +
              'Finish it if you still have the QR code open, or try again in a few minutes.'
            : '',
      );
      invalidate();
    },
  });

  // (The cross-login "pending acknowledgement" drain that used to live here is
  // gone: enrollment keeps the session alive, so the recovery-codes page
  // acknowledges directly. Nothing has to survive a re-login any more.)

  const cancelWizard = () => {
    // Cancelling AFTER /mfa/setup ran (the setup-verify step) leaves the account
    // in `enabling` server-side. Roll it back immediately so the resting panel
    // shows the true state right away instead of waiting out the ~10-min
    // staleness cutoff. Earlier steps have no server state to undo — just
    // refetch. (See rollbackEnablingIfAny for the re-enrollment carve-out.)
    if (wizard.kind === 'setup-verify') {
      rollbackEnablingIfAny(wizard.reenroll === true);
    } else {
      invalidate();
    }
    setWizard({ kind: 'idle' });
  };

  const startSetup = (reenroll = false) => {
    setupMutation.reset();
    verifyMutation.reset();
    acknowledgeMutation.reset();
    setWizard({ kind: 'setup-password', reenroll });
  };

  const startStepUp = (action: 'disable' | 'regenerate') => {
    disableMutation.reset();
    regenerateMutation.reset();
    acknowledgeMutation.reset();
    setWizard({ kind: 'stepup', action });
  };

  return (
    <Card data-testid="mfa-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" /> Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Protect your account with a time-based code from an authenticator app, in addition to your
          password.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/*
          The wizard steps render from local state and never read `data`, so they
          come BEFORE the status-query health check — a background status refetch
          failing (e.g. on window focus) must never replace an in-flight
          enrollment/regeneration (and destroy freshly-shown codes) with an error.
        */}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : wizard.kind === 'setup-password' ? (
          <SetupPasswordStep
            reenroll={wizard.reenroll}
            // Same signal that raises the post-recovery nudge above, so the
            // form opens on the factor that nudge implies the user still has.
            // Passed as a prop (not read from the query inside the step) so the
            // value is settled at mount — useState only honours its seed once.
            preferRecoveryCode={Boolean(data?.recoveryCodeUsedAt)}
            pending={setupMutation.isPending}
            error={setupMutation.error ? errMessage(setupMutation.error) : ''}
            onCancel={cancelWizard}
            onSubmit={(v) => setupMutation.mutate(v)}
          />
        ) : wizard.kind === 'setup-verify' ? (
          <SetupVerifyStep
            secretCode={wizard.secretCode}
            otpauthUri={wizard.otpauthUri}
            accountLabel={wizard.accountLabel}
            issuer={wizard.issuer}
            reenroll={wizard.reenroll}
            pending={verifyMutation.isPending}
            error={verifyMutation.error ? errMessage(verifyMutation.error) : ''}
            onCancel={cancelWizard}
            onSubmit={(code) => verifyMutation.mutate(code)}
          />
        ) : wizard.kind === 'codes' ? (
          <RecoveryCodesDisplay
            codes={wizard.codes}
            account={user?.email}
            generatedAt={wizard.generatedAt}
            renderAction={(confirmed) => (
              <div className="space-y-2">
                {acknowledgeMutation.error && (
                  <p className="text-sm text-destructive">
                    {errMessage(acknowledgeMutation.error)}
                  </p>
                )}
                <Button
                  disabled={!confirmed || acknowledgeMutation.isPending}
                  onClick={() => acknowledgeMutation.mutate()}
                >
                  {acknowledgeMutation.isPending && (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  )}{' '}
                  Done
                </Button>
              </div>
            )}
          />
        ) : wizard.kind === 'stepup' ? (
          <StepUpStep
            action={wizard.action}
            pending={
              wizard.action === 'disable' ? disableMutation.isPending : regenerateMutation.isPending
            }
            error={
              wizard.action === 'disable'
                ? disableMutation.error
                  ? errMessage(disableMutation.error)
                  : ''
                : regenerateMutation.error
                  ? errMessage(regenerateMutation.error)
                  : ''
            }
            onCancel={cancelWizard}
            onSubmit={(currentPassword, factor) => {
              if (wizard.action === 'disable') disableMutation.mutate({ currentPassword, factor });
              else regenerateMutation.mutate({ currentPassword, factor });
            }}
          />
        ) : isError || !data ? (
          <p className="text-sm text-muted-foreground">
            We couldn’t load your two-factor status. Refresh the page to try again.
          </p>
        ) : (
          <StatusPanel
            status={data}
            privilegedRequired={privilegedRequired}
            onStartSetup={() => startSetup(false)}
            onRegenerate={() => startStepUp('regenerate')}
            onReenroll={() => startSetup(true)}
            onDismissRecoveryNotice={() => dismissRecoveryNoticeMutation.mutate()}
            dismissingRecoveryNotice={dismissRecoveryNoticeMutation.isPending}
            onDisable={() => startStepUp('disable')}
            ackError={acknowledgeMutation.error ? errMessage(acknowledgeMutation.error) : ''}
            onRepair={() => repairMutation.mutate()}
            repairing={repairMutation.isPending}
            repairError={repairMutation.error ? errMessage(repairMutation.error) : ''}
            repairNotice={repairNotice}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Status-driven resting panel (wizard idle)
// ---------------------------------------------------------------------------

function StatusPanel({
  status,
  privilegedRequired,
  onStartSetup,
  onRegenerate,
  onReenroll,
  onDismissRecoveryNotice,
  dismissingRecoveryNotice,
  onDisable,
  ackError,
  onRepair,
  repairing,
  repairError,
  repairNotice,
}: {
  status: MfaStatusResponse;
  privilegedRequired: boolean;
  onStartSetup: () => void;
  onRegenerate: () => void;
  onReenroll: () => void;
  onDismissRecoveryNotice: () => void;
  dismissingRecoveryNotice: boolean;
  onDisable: () => void;
  ackError: string;
  onRepair: () => void;
  repairing: boolean;
  repairError: string;
  repairNotice: string;
}) {
  const { state } = status;

  // Privileged roles must have MFA on. When they don't, lead with a required
  // notice; if the server surfaced a live gate deadline, show the countdown /
  // enforced block (the server-side gate is the real enforcement — this makes the
  // deadline visible so a privileged user isn't surprised by a lockout).
  // Only claim MFA is REQUIRED when the gate actually requires it. The gate is
  // absent for non-privileged users and `off` during rollout; saying "required"
  // then contradicts the live configuration and pressures people into a security
  // change nothing is asking for yet. `grace` and `enforced` are genuine.
  const gateRequiresMfa =
    status.privilegedMfa?.mode === 'grace' || status.privilegedMfa?.mode === 'enforced';
  const requiredNotice =
    privilegedRequired && gateRequiresMfa && state !== 'enabled' ? (
      <div className="flex items-start gap-3 rounded-md bg-destructive/10 border border-destructive/20 p-3">
        <ShieldAlert className="h-5 w-5 flex-shrink-0 text-destructive" />
        <p className="text-sm text-destructive">{privilegedRequiredCopy(status.privilegedMfa)}</p>
      </div>
    ) : null;

  if (state === 'enabled') {
    return (
      <EnabledPanel
        status={status}
        onRegenerate={onRegenerate}
        onReenroll={onReenroll}
        onDismissRecoveryNotice={onDismissRecoveryNotice}
        dismissingRecoveryNotice={dismissingRecoveryNotice}
        onDisable={onDisable}
        // A mandatory-MFA role cannot turn the factor off — the server refuses
        // it (403 MFA_REQUIRED_FOR_ROLE), and disabling the control here keeps
        // the user out of a state where the middleware would evict them on their
        // next request with no way back.
        mfaRequiredForRole={privilegedRequired && gateRequiresMfa}
        ackError={ackError}
        onRepair={onRepair}
        repairing={repairing}
        repairError={repairError}
      />
    );
  }

  if (state === 'recovery_pending') {
    return (
      <div className="space-y-4">
        {requiredNotice}
        <div className="flex items-start gap-3 rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
          <ShieldAlert className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Two-factor authentication is <strong>off</strong>. It was removed by a recovery code or
            a support reset. Set it up again to keep your account secure.
          </p>
        </div>
        <Button onClick={onStartSetup} disabled={status.enrollmentEnabled === false}>
          <ShieldCheck className="h-4 w-4 mr-1.5" /> Set up two-factor authentication
        </Button>
        {status.enrollmentEnabled === false && (
          // Same gate the ordinary disabled panel applies. Without it a
          // recovered or support-reset user — often a privileged one being told
          // MFA is REQUIRED — is pointed at an action guaranteed to 403
          // MFA_ENROLLMENT_DISABLED while enrollment is paused.
          <p className="text-sm text-muted-foreground" role="status">
            Setting up two-factor authentication isn’t available right now. Please check back soon.
          </p>
        )}
      </div>
    );
  }

  if (state === 'enabling' || state === 'disabling' || state === 'resetting') {
    // NOT "it resets automatically in about 10 minutes" — that was false, and
    // it sent people away to wait for something that never happens. Nothing
    // sweeps a stranded `enabling`: `reconcileMfaState` has exactly one caller
    // (`POST /auth/mfa/repair`, i.e. this button), there is no worker job, and
    // `/setup` refuses outright while `enabling` regardless of age. So waiting
    // accomplishes nothing; clicking Repair is the whole remedy. The ~10-minute
    // cutoff is real but is a lower bound on when Repair can act, not a timer
    // that fixes anything on its own — and the too-soon case now reports itself
    // through repairNotice rather than looking like a dead button.
    const copy =
      state === 'enabling'
        ? 'You started setting up two-factor authentication but didn’t finish. Repair it to clear that, then you can set it up again.'
        : 'A two-factor change didn’t finish. Repair it to get your account back to a clean state.';
    return (
      <div className="space-y-4">
        {requiredNotice}
        <div className="flex items-start gap-3 rounded-md bg-muted px-3 py-2">
          <Wrench className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{copy}</p>
        </div>
        {repairNotice && <p className="text-sm text-destructive">{repairNotice}</p>}
        {repairError && <p className="text-sm text-destructive">{repairError}</p>}
        <Button variant="outline" onClick={onRepair} disabled={repairing}>
          {repairing ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="h-4 w-4 mr-1.5" />
          )}
          Repair
        </Button>
      </div>
    );
  }

  // disabled (default)
  // Absent `enrollmentEnabled` (older API response) is treated as available so
  // an enrolled user managing MFA is never blocked; only an explicit `false`
  // (the default flag-off rollout state) disables the setup action, since the
  // server would otherwise 403 MFA_ENROLLMENT_DISABLED.
  const enrollmentAvailable = status.enrollmentEnabled !== false;
  return (
    <div className="space-y-4">
      {requiredNotice}
      <p className="text-sm text-muted-foreground">
        Two-factor authentication is <strong>off</strong>. Turn it on to require a one-time code at
        sign-in — even if someone learns your password.
      </p>
      <Button onClick={onStartSetup} disabled={!enrollmentAvailable}>
        <ShieldCheck className="h-4 w-4 mr-1.5" /> Set up two-factor authentication
      </Button>
      {!enrollmentAvailable && (
        <p className="text-sm text-muted-foreground" role="status">
          Setting up two-factor authentication isn’t available right now. Please check back soon.
        </p>
      )}
    </div>
  );
}

function EnabledPanel({
  status,
  onRegenerate,
  onReenroll,
  onDismissRecoveryNotice,
  dismissingRecoveryNotice,
  onDisable,
  mfaRequiredForRole,
  ackError,
  onRepair,
  repairing,
  repairError,
}: {
  status: MfaStatusResponse;
  onRegenerate: () => void;
  onReenroll: () => void;
  onDismissRecoveryNotice: () => void;
  dismissingRecoveryNotice: boolean;
  onDisable: () => void;
  mfaRequiredForRole: boolean;
  ackError: string;
  onRepair: () => void;
  repairing: boolean;
  repairError: string;
}) {
  const { recoveryCodesRemaining, recoveryCodesAcknowledged, drift, recoveryCodeUsedAt } = status;
  const lowCodes = recoveryCodesRemaining <= 3;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-500/10 text-green-700 dark:text-green-400 px-2.5 py-0.5 text-xs font-medium">
          <ShieldCheck className="h-3.5 w-3.5" /> On
        </span>
        <span className="text-sm text-muted-foreground">
          You’ll be asked for a code from your authenticator app when you sign in.
        </span>
      </div>

      {/* Post-recovery nudge. Signing in with a backup code means the user is
          running degraded — their authenticator is lost, unreachable, or drifted
          — but it is NOT a problem we should fix on their behalf: MFA is intact
          and forcing a re-enrollment would be the old, destructive behaviour by
          another name. So: offer, don't impose. Both actions are terminal, so
          the prompt can always be made to go away. */}
      {recoveryCodeUsedAt && (
        <div
          className="flex items-start gap-3 rounded-md bg-blue-500/10 border border-blue-500/20 p-3"
          data-testid="mfa-recovery-used-notice"
        >
          <KeyRound className="h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
          <div className="space-y-2">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              You signed in with a backup code. If you’ve lost access to your authenticator app,
              link a new one now — you’ll also get a fresh set of ten backup codes.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={onReenroll}>
                <ShieldCheck className="h-4 w-4 mr-1.5" /> Link a new authenticator
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDismissRecoveryNotice}
                disabled={dismissingRecoveryNotice}
              >
                {dismissingRecoveryNotice && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                My authenticator still works
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Turning on 2FA does not sign out sessions already open on other devices
          — an honest note, since many people enable it expecting to evict a
          suspected intruder. Logging out is the keep-nothing-open action
          (`/auth/logout` global-signs-out every device). */}
      <p className="text-xs text-muted-foreground">
        Turning on two-factor didn’t sign out your other devices. To close every open session, log
        out and sign back in.
      </p>

      {drift && (
        <div className="flex items-start gap-3 rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="space-y-2">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              Your two-factor settings look out of sync. Repairing will re-check and fix them.
            </p>
            {repairError && <p className="text-sm text-destructive">{repairError}</p>}
            <Button size="sm" variant="outline" onClick={onRepair} disabled={repairing}>
              {repairing ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Wrench className="h-4 w-4 mr-1.5" />
              )}
              Repair
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-md border border-border p-3 space-y-2">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Recovery codes</span>
        </div>
        <p className={`text-sm ${lowCodes ? 'text-destructive' : 'text-muted-foreground'}`}>
          {recoveryCodesRemaining} unused code{recoveryCodesRemaining === 1 ? '' : 's'} remaining.
          {lowCodes && ' Regenerate soon so you don’t get locked out.'}
        </p>

        {!recoveryCodesAcknowledged && (
          <div className="flex items-start gap-3 rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="space-y-2">
              {/* Reaching this state means the codes were generated but never
                  displayed — enrollment now shows and acknowledges them in one
                  step, so an unacknowledged set is OUR fault, not a user
                  omission. Say so, and offer the only action that can actually
                  help: regenerate. "I've saved them" is deliberately absent —
                  the user cannot have saved codes they were never shown, and
                  offering it would let them dismiss the warning into a state
                  where they hold no usable codes at all. */}
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Your recovery codes were generated but not displayed. Generate a new set to view and
                save them.
              </p>
              {ackError && <p className="text-sm text-destructive">{ackError}</p>}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={onRegenerate}>
                  Regenerate codes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 pt-1">
        <Button variant="outline" onClick={onRegenerate}>
          <KeyRound className="h-4 w-4 mr-1.5" /> Regenerate recovery codes
        </Button>
        <Button
          variant="outline"
          className="text-destructive"
          onClick={onDisable}
          disabled={mfaRequiredForRole}
          title={
            mfaRequiredForRole
              ? 'Two-factor authentication is required for your account role.'
              : undefined
          }
        >
          <ShieldAlert className="h-4 w-4 mr-1.5" /> Turn off two-factor authentication
        </Button>
      </div>

      {mfaRequiredForRole && (
        <p className="text-xs text-muted-foreground">
          Two-factor authentication is required for your account role and can’t be turned off. If
          your role changes, this becomes optional.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enrollment wizard steps
// ---------------------------------------------------------------------------

function SetupPasswordStep({
  reenroll,
  preferRecoveryCode = false,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  reenroll: boolean;
  /**
   * Seed the factor control to the backup-code field instead of the
   * authenticator one. Set when a recovery code was used to sign in — see the
   * call site for why.
   */
  preferRecoveryCode?: boolean;
  pending: boolean;
  error: string;
  onCancel: () => void;
  onSubmit: (v: { currentPassword: string; totpCode?: string; recoveryCode?: string }) => void;
}) {
  const [password, setPassword] = useState('');
  // Re-enrollment takes over the account's second factor, so it must re-prove
  // that factor — a session plus a password is not enough (the server refuses).
  // Same control as `/disable` and regenerate: authenticator code or backup code.
  //
  // WHICH one is offered first is presentation, not policy. Arriving here from
  // the post-recovery nudge means the user just proved, by using a backup code,
  // that their authenticator is gone — so defaulting to "authenticator code"
  // asks for the single factor we have positive evidence they cannot produce.
  // Costs nothing to prefer the backup code: step-up VALIDATES without
  // consuming (verifyRecoveryCodeWithoutConsuming), and a successful
  // re-enrollment reissues all ten codes anyway.
  const factor = useSecondFactor(preferRecoveryCode ? 'recovery' : 'totp');
  const proofReady = !reenroll || factor.ready;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!password || !proofReady) return;
        onSubmit({ currentPassword: password, ...(reenroll ? factor.factor : {}) });
      }}
    >
      <p className="text-sm text-muted-foreground">
        {reenroll
          ? 'Confirm it’s you before linking a new authenticator app. Your current app will stop working once you finish.'
          : 'Confirm your password to start setting up two-factor authentication.'}
      </p>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="space-y-2">
        <Label htmlFor="mfa-setup-password">Current password</Label>
        <PasswordInput
          id="mfa-setup-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          disabled={pending}
        />
      </div>

      {reenroll && (
        <>
          <SecondFactorFields
            idPrefix="mfa-reenroll"
            pending={pending}
            factor={factor}
            totpLabel="Code from your current authenticator"
          />
          {factor.kind === 'recovery' && (
            <p className="text-xs text-muted-foreground">
              This backup code isn’t spent — finishing here replaces the whole set with ten fresh
              ones.
            </p>
          )}
        </>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={pending || !password || !proofReady}>
          {pending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Continue
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * Base32 in groups of four. Purely for reading and typing accuracy — the copy
 * button and the QR both carry the UNGROUPED secret, because an app that
 * doesn't strip whitespace on paste would reject the pretty version.
 */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * The manual-entry key. Always visible on every device: it is the fallback for
 * BOTH of the other two transports failing — no camera to scan the QR, and no
 * installed app to answer the `otpauth://` deep link. Hiding it behind either
 * one would strand the exact users it exists for.
 */
function ManualKeyBlock({ secretCode }: { secretCode: string }) {
  const [copied, setCopied] = useState(false);
  const canCopy =
    typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function';
  const copyKey = async () => {
    // Only report success on a real write — never a false "Copied" when the
    // Clipboard API is unavailable (insecure context / older browser). The key
    // stays visible for manual entry either way.
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(secretCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Denied — nothing to do; the key remains selectable on screen.
    }
  };

  return (
    <div className="w-full space-y-1">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-muted-foreground">Or enter this key manually</Label>
        {canCopy && (
          <button
            type="button"
            onClick={copyKey}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            aria-label="Copy setup key"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        )}
      </div>
      {/* Grouped for the eye only. The raw secret is never duplicated into an
          attribute — the copy handler closes over `secretCode` directly, so
          there is exactly one rendering of it on the page. */}
      <code
        data-testid="mfa-setup-key"
        className="block break-all rounded-md bg-muted px-3 py-2 text-sm font-mono tracking-wider"
      >
        {groupSecret(secretCode)}
      </code>
      <p className="text-xs text-muted-foreground">
        Spaces are for readability — type or paste it without them if your app is fussy.
      </p>
    </div>
  );
}

function QrBlock({ otpauthUri }: { otpauthUri: string }) {
  return (
    <div className="flex justify-center">
      <div className="bg-white p-3 rounded-lg inline-block">
        <QRCodeSVG value={otpauthUri} size={176} />
      </div>
    </div>
  );
}

/**
 * Same-device enrollment. A phone cannot photograph its own screen, so the
 * mobile-native equivalent of scanning is handing the `otpauth://` URI to the
 * OS: every mainstream TOTP app (Google Authenticator, Authy, Microsoft
 * Authenticator, 1Password, Bitwarden) registers for that scheme, and the entry
 * is created with the secret, issuer and account name already filled in.
 *
 * Two failure modes, both handled by what is NOT here rather than by detection:
 *
 *   - No app installed. The scheme resolves to nothing and the tap silently
 *     does nothing — there is no reliable cross-browser way to observe that, so
 *     no attempt is made to. The manual key and the QR stay on screen instead,
 *     which is why the note below points at them.
 *   - Several apps installed. The OS shows a chooser. That is correct
 *     behaviour, not an error, so nothing assumes a single target.
 *
 * A plain anchor, deliberately: `window.location` assignment to an unhandled
 * custom scheme throws or logs in some browsers, and `target="_blank"` gets the
 * navigation blocked in others.
 */
function DeepLinkBlock({ otpauthUri }: { otpauthUri: string }) {
  return (
    <div className="space-y-1.5">
      <Button asChild className="w-full">
        <a href={otpauthUri} data-testid="mfa-deep-link">
          <Smartphone className="h-4 w-4 mr-1.5" /> Add to authenticator app
        </a>
      </Button>
      <p className="text-xs text-muted-foreground">
        Opens your authenticator app with this account ready to add. If you have more than one,
        you’ll be asked which. If nothing happens — or you’re asked to pick an app and none of them
        are an authenticator — you don’t have one installed yet; use the key or QR code instead.
      </p>
    </div>
  );
}

function SetupVerifyStep({
  secretCode,
  otpauthUri,
  accountLabel,
  issuer,
  reenroll,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  secretCode: string;
  otpauthUri: string;
  accountLabel?: string;
  issuer?: string;
  reenroll: boolean;
  pending: boolean;
  error: string;
  onCancel: () => void;
  onSubmit: (code: string) => void;
}) {
  const [code, setCode] = useState('');
  // Device capability, not viewport size — see the hook. Decides ORDER only:
  // all three transports render either way, because the right one is a matter
  // of the user's setup (a phone enrolling against a desktop authenticator is
  // ordinary) and not something we can detect.
  const touchDevice = useCoarsePointer();

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.length === 6) onSubmit(code);
      }}
    >
      {/* On a re-enrollment the app ends up holding TWO Fias entries and only
          the new one works. Say so explicitly and say which to use — a code
          from the old entry is rejected (the server checks it against the key
          on this screen), and a user who doesn't know there are two will read
          that rejection as "2FA is broken". The entries are now distinguishable
          by account name, which is what makes "use the new one" actionable. */}
      {reenroll && (
        <div className="flex items-start gap-3 rounded-md bg-amber-500/10 border border-amber-500/20 p-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            This <strong>replaces</strong> your current authenticator. Your app will show both the
            old and the new entry — confirm with the new one below, then delete the old one. It
            stops working the moment you finish here.
          </p>
        </div>
      )}

      <ol className="text-sm text-muted-foreground list-decimal list-inside space-y-1">
        <li>Open your authenticator app (Google Authenticator, 1Password, Authy…).</li>
        <li>
          {touchDevice
            ? 'Tap the button below to add this account, or enter the key by hand.'
            : 'Scan the QR code below, or enter the key by hand.'}
        </li>
        <li>Enter the 6-digit code it shows to confirm.</li>
      </ol>

      {/* Name the entry the user is about to create, so that when several Fias
          entries exist they can match this one exactly instead of guessing —
          which is the whole reason the server makes the name unique. The date
          is called out as UTC because it is: someone far enough east sees
          "yesterday" and would otherwise read that as the wrong entry. */}
      {accountLabel && (
        <p className="text-xs text-muted-foreground" data-testid="mfa-account-label">
          It will appear in your app as{' '}
          <span className="font-mono text-foreground">
            {issuer ?? 'Fias'} — {accountLabel}
          </span>
          . The date is UTC, so it may not match your local calendar.
        </p>
      )}

      {/* Order by context; content is identical. On a phone the QR is the one
          option that cannot work on THIS device, so it moves into a disclosure
          — still one tap away for the genuinely common cross-device case
          (enrolling on a phone against a desktop or hardware authenticator). */}
      {touchDevice ? (
        <div className="space-y-4">
          <DeepLinkBlock otpauthUri={otpauthUri} />
          <ManualKeyBlock secretCode={secretCode} />
          <details className="rounded-md border border-border px-3 py-2">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Scan a QR code from another device instead
            </summary>
            <div className="pt-3">
              <QrBlock otpauthUri={otpauthUri} />
            </div>
          </details>
        </div>
      ) : (
        <div className="space-y-4">
          <QrBlock otpauthUri={otpauthUri} />
          <ManualKeyBlock secretCode={secretCode} />
          {/* Kept on desktop too: 1Password, Bitwarden and Authy register the
              scheme on desktop as well, and it beats reaching for a phone. */}
          <DeepLinkBlock otpauthUri={otpauthUri} />
        </div>
      )}

      {error && (
        <div className="space-y-1">
          <p className="text-sm text-destructive">{error}</p>
          <p className="text-xs text-muted-foreground">
            If the code keeps failing, check that your phone’s clock is set to update automatically
            — authenticator codes are time-based.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="mfa-verify-code">Verification code</Label>
        <Input
          id="mfa-verify-code"
          type="text"
          inputMode="numeric"
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(keepDigits(e.target.value))}
          required
          autoComplete="one-time-code"
          maxLength={6}
          disabled={pending}
          className="h-11 text-center font-mono tracking-[0.4em] text-lg"
        />
      </div>

      <div className="flex gap-2">
        <Button type="submit" disabled={pending || code.length !== 6}>
          {pending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Turn on
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Step-up (password + current TOTP) — for disable & regenerate
// ---------------------------------------------------------------------------

/**
 * The second-factor input shared by every step-up surface (disable, regenerate,
 * re-enroll). One control, because the server accepts one primitive: a live
 * authenticator code OR a backup code. Offering only TOTP would trap the user
 * these flows exist for — the one whose authenticator is gone.
 */
function useSecondFactor(initialKind: 'totp' | 'recovery' = 'totp') {
  // `initialKind` only seeds the FIRST render — callers must therefore mount
  // this behind a resolved status query, or the seed silently falls back to
  // 'totp'. The one caller that passes it (SetupPasswordStep) renders under the
  // card's `isLoading` guard, so the value is known by mount.
  const [kind, setKind] = useState<'totp' | 'recovery'>(initialKind);
  const [totpCode, setTotpCode] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');

  const ready = kind === 'totp' ? totpCode.length === 6 : recoveryCode.trim().length > 0;
  const factor: MfaStepUpFactor =
    kind === 'totp' ? { totpCode } : { recoveryCode: recoveryCode.trim() };

  return { kind, setKind, totpCode, setTotpCode, recoveryCode, setRecoveryCode, ready, factor };
}

function SecondFactorFields({
  idPrefix,
  pending,
  factor,
  totpLabel = 'Authentication code',
}: {
  idPrefix: string;
  pending: boolean;
  factor: ReturnType<typeof useSecondFactor>;
  totpLabel?: string;
}) {
  if (factor.kind === 'totp') {
    return (
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-totp`}>{totpLabel}</Label>
        <Input
          id={`${idPrefix}-totp`}
          type="text"
          inputMode="numeric"
          placeholder="123456"
          value={factor.totpCode}
          onChange={(e) => factor.setTotpCode(keepDigits(e.target.value))}
          autoComplete="one-time-code"
          maxLength={6}
          disabled={pending}
          className="text-center font-mono tracking-[0.4em]"
        />
        <button
          type="button"
          className="text-sm text-primary hover:underline"
          onClick={() => factor.setKind('recovery')}
          disabled={pending}
        >
          I can’t use my authenticator — use a backup code instead
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <Label htmlFor={`${idPrefix}-recovery`}>Backup code</Label>
      <Input
        id={`${idPrefix}-recovery`}
        type="text"
        placeholder="XXXX-XXXX-XXXX-XXXX"
        value={factor.recoveryCode}
        onChange={(e) => factor.setRecoveryCode(e.target.value)}
        autoComplete="one-time-code"
        disabled={pending}
        className="text-center font-mono tracking-widest"
      />
      <button
        type="button"
        className="text-sm text-primary hover:underline"
        onClick={() => factor.setKind('totp')}
        disabled={pending}
      >
        Use my authenticator code instead
      </button>
    </div>
  );
}

function StepUpStep({
  action,
  pending,
  error,
  onCancel,
  onSubmit,
}: {
  action: 'disable' | 'regenerate';
  pending: boolean;
  error: string;
  onCancel: () => void;
  onSubmit: (password: string, factor: MfaStepUpFactor) => void;
}) {
  const [password, setPassword] = useState('');
  const factor = useSecondFactor();

  const title =
    action === 'disable' ? 'Turn off two-factor authentication' : 'Regenerate recovery codes';
  const cta = action === 'disable' ? 'Turn off' : 'Regenerate';

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (password && factor.ready) onSubmit(password, factor.factor);
      }}
    >
      <p className="text-sm font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">
        Confirm it’s you: enter your password and a code from your authenticator app — or a backup
        code if you can’t reach it.
        {action === 'regenerate' &&
          ' A backup code used here isn’t spent; this replaces the whole set anyway.'}
      </p>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="space-y-2">
        <Label htmlFor="mfa-stepup-password">Current password</Label>
        <PasswordInput
          id="mfa-stepup-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
          disabled={pending}
        />
      </div>

      <SecondFactorFields idPrefix="mfa-stepup" pending={pending} factor={factor} />

      <div className="flex gap-2">
        <Button
          type="submit"
          variant={action === 'disable' ? 'destructive' : 'default'}
          disabled={pending || !password || !factor.ready}
        >
          {pending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} {cta}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
